import { useMemo } from "react";
import ReactSelect, { type StylesConfig } from "react-select";
import type { JurisdictionResponse } from "@/lib/types";

type Option = { label: string; value: string | null };
type Group = { label: string; options: Option[] };

interface Props {
  setJurisdiction: (value: string) => void;
  jurisdiction: string;
  jurisdictions: JurisdictionResponse[] | undefined;
  yourJurisdictions: JurisdictionResponse[] | undefined;
  isDisabled?: boolean;
}

export const customSelectStyles: StylesConfig<Option, false, Group> = {
  control: (baseStyles, state) => ({
    ...baseStyles,
    borderColor: "rgb(128, 135, 136)",
    backgroundColor: state.isDisabled ? "#CCCFCF" : "white",
    boxShadow: !state.isFocused ? "0" : "0 0 0 0.25rem rgba(4, 65, 84, 0.25)",
    "&:hover": {
      borderColor: "rgb(128, 135, 136)",
    },
    fontSize: "16px",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 3, //some stuff in the sdr flow was appearing on top
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (baseStyles) => ({
    ...baseStyles,
    color: "#4D5758",
    "& svg": {
      width: 22,
      paddingRight: "4px",
    },
    "&:hover": {
      color: "#4D5758",
    },
  }),
  option: (provided) => ({
    ...provided,
    padding: "2px 8px",
  }),
  singleValue: (provided, state) => ({
    ...provided,
    color: state.isDisabled ? "#4d5758" : "inherit",
  }),
  group: (provided) => ({
    ...provided,
    padding: "0px",
  }),
  groupHeading: (provided) => {
    return {
      ...provided,
      fontWeight: "700",
      borderTop: "1pt solid #cccccc",
      borderBottom: "1pt solid #cccccc",
    };
  },
  placeholder: (provided) => ({
    ...provided,
    color: "var(--placeholder-color)",
  }),
};

export default function JurisdictionSelect(props: Props) {
  const {
    setJurisdiction,
    jurisdiction,
    jurisdictions,
    yourJurisdictions,
    isDisabled,
  } = props;

  const getOptions = useMemo(() => {
    if (!jurisdictions) {
      return;
    }

    const opts: Group[] = [];

    opts.push({
      label: "Your Jurisdictions",
      options:
        yourJurisdictions?.map((j) => {
          return { label: j.friendlyName || "", value: j.id || "" };
        }) || [],
    });

    opts.push({
      label: "Additional Jurisdictions",
      options:
        jurisdictions
          .filter((j) => !yourJurisdictions?.some((yj) => yj.id === j.id))
          .map((j) => {
            return { label: j.friendlyName || "", value: j.id || "" };
          }) || [],
    });

    return opts;
  }, [jurisdictions, yourJurisdictions]);

  const handleChange = (value: Option | null) => {
    if (!value) {
      return;
    }
    setJurisdiction(value.value ?? "");
  };

  const formatGroupLabel = (data: Group) => {
    return <div>{data.label}</div>;
  };

  const findOptionByJurisdictionId = (
    options: Group[],
    jurisdictionId: string,
  ) => {
    for (const group of options) {
      const found = group.options.find(
        (option: Option) => option.value === jurisdictionId,
      );
      if (found) {
        return found;
      }
    }
    return null;
  };

  return (
    <ReactSelect<Option, false, Group>
      key={jurisdiction}
      className="select"
      id="venue"
      options={getOptions || []}
      styles={customSelectStyles}
      value={findOptionByJurisdictionId(getOptions || [], jurisdiction)}
      onChange={handleChange}
      formatGroupLabel={formatGroupLabel}
      isDisabled={isDisabled}
      theme={(theme) => ({
        ...theme,
        colors: {
          ...theme.colors,
          primary: "#074f6b",
          primary25: "#efefea",
        },
      })}
      placeholder="Required please select..."
      menuPlacement="auto"
    />
  );
}
